import { PreviewTask, previewTasksAdapter } from "@jugl-web/rest-api/tasks";
import { useMemo } from "react";
import { extractBoardIdFromTasksSource } from "../../utils";
import { useTaskBoardsQuery } from "../useTaskBoardsQuery";
import { useTaskCustomFields } from "../useTaskCustomFields";
import { INITIAL_TASK_FILTERS_STATE } from "../useTaskFiltersState";
import { useTaskLabels } from "../useTaskLabels";
import { useTaskSearchQueryFilters } from "../useTaskSearchQueryFilters";
import { useTaskSelectors } from "../useTaskSelectors";
import { useTaskStatusesGetters } from "../useTaskStatusesGetters";
import { useTasksQuery } from "../useTasksQuery";
import { sortTasksByMultipleRules, sortTasksInitially } from "./sortUtils";
import { UseTasksOptions } from "./types";
import {
  createAssigneesFilterMatcher,
  createBoardsFilterMatcher,
  createCustomFieldsFilterMatcher,
  createCustomersFilterMatcher,
  createIsOverdueFilterMatcher,
  createLabelsFilterMatcher,
  createPrioritiesFilterMatcher,
  createSearchQueryFilterMatcher,
  createStatusesFilterMatcher,
  withFutureTasks,
} from "./utils";

export const useTasks = ({
  meId,
  entityId,
  source,
  searchQuery,
  searchQueryFilters,
  filters = INITIAL_TASK_FILTERS_STATE,
  showFuture = false,
  forceShowCompleted = false,
  sortingConfig,
}: UseTasksOptions) => {
  const boardId = extractBoardIdFromTasksSource(source) || null;
  const { cachedBoards: allBoards } = useTaskBoardsQuery({ entityId });
  const { getStatusByBoard } = useTaskStatusesGetters({
    entityId,
  });

  const { data } = useTasksQuery({ entityId, source });

  const { getTaskSearchQueryFilters } = useTaskSearchQueryFilters({
    entityId,
    source,
  });

  const {
    createSelectTasksByDueDateInterval,
    createSelectOverdueTasks,
    createSelectNoDueDateTasks,
    createSelectTasksByLabelId,
    createSelectTasksByStatusId,
    createSelectTasksByStatusName,
    createSelectTasksByReporteeId,
    createSelectTasksByAssigneeId,
    createSelectTasksByBoardId,
    createSelectTasksFromRestrictedBoards,
    createSelectTasksByCustomerId,
    createSelectTasksByPriority,
    createSelectTasksByCustomDropdownFieldValue,
    createSelectTasksByCalendarDay,
  } = useTaskSelectors({ entityId, boardId, meId });

  const { boardCustomFields } = useTaskCustomFields({ entityId, boardId });

  const { labels } = useTaskLabels({ entityId });

  const allTasks = useMemo(() => {
    const regularTasks = previewTasksAdapter
      .getSelectors()
      .selectAll(data ?? previewTasksAdapter.getInitialState());

    if (showFuture) {
      return withFutureTasks(regularTasks);
    }
    return regularTasks;
  }, [data, showFuture]);

  const allSortedTasks = useMemo(() => {
    const initiallySortedTasks = sortTasksInitially(allTasks);

    // Then, sort by user-applied sorting rules.
    const sortedTasks = sortTasksByMultipleRules({
      tasks: initiallySortedTasks,
      rules: sortingConfig ?? [],
      customFields: boardCustomFields,
      labels,
      boards: allBoards,
      getStatusByBoard,
    });

    return sortedTasks;
  }, [
    allTasks,
    sortingConfig,
    boardCustomFields,
    labels,
    allBoards,
    getStatusByBoard,
  ]);

  const allTaskEntities = useMemo(
    () =>
      previewTasksAdapter
        .getSelectors()
        .selectEntities(data ?? previewTasksAdapter.getInitialState()),
    [data]
  );

  const filteredTasks = useMemo(() => {
    const filterMatchers = [
      createSearchQueryFilterMatcher(
        searchQuery,
        getTaskSearchQueryFilters(searchQueryFilters || [])
      ),
      createAssigneesFilterMatcher(filters.assignees),
      createCustomersFilterMatcher(filters.customers),
      createCustomFieldsFilterMatcher(filters.customFields),
      createIsOverdueFilterMatcher(filters.isOverdue),
      createBoardsFilterMatcher(filters.boards),
      createLabelsFilterMatcher(filters.labels),
      createPrioritiesFilterMatcher(filters.priorities),
      createStatusesFilterMatcher(filters.statuses, forceShowCompleted),
    ];

    return allSortedTasks.reduce<PreviewTask[]>((acc, task) => {
      const matchesFilters = filterMatchers.every((fn) => fn(task));

      if (!matchesFilters) {
        return acc;
      }

      return [...acc, task];
    }, []);
  }, [
    searchQueryFilters,
    allSortedTasks,
    filters,
    searchQuery,
    forceShowCompleted,
    getTaskSearchQueryFilters,
  ]);

  const selectTasksByDueDateInterval = useMemo(
    () => createSelectTasksByDueDateInterval(filteredTasks),
    [createSelectTasksByDueDateInterval, filteredTasks]
  );

  const selectTasksByCalendarDay = useMemo(
    () => createSelectTasksByCalendarDay(filteredTasks),
    [createSelectTasksByCalendarDay, filteredTasks]
  );

  const selectOverdueTasks = useMemo(
    () => createSelectOverdueTasks(filteredTasks),
    [createSelectOverdueTasks, filteredTasks]
  );

  const selectNoDueDateTasks = useMemo(
    () => createSelectNoDueDateTasks(filteredTasks),
    [createSelectNoDueDateTasks, filteredTasks]
  );

  const selectTasksByLabelId = useMemo(
    () => createSelectTasksByLabelId(filteredTasks),
    [createSelectTasksByLabelId, filteredTasks]
  );

  const selectTasksByStatusId = useMemo(
    () => createSelectTasksByStatusId(filteredTasks),
    [createSelectTasksByStatusId, filteredTasks]
  );

  const selectTasksByStatusName = useMemo(
    () => createSelectTasksByStatusName(filteredTasks),
    [createSelectTasksByStatusName, filteredTasks]
  );

  const selectTasksByReporteeId = useMemo(
    () => createSelectTasksByReporteeId(filteredTasks),
    [createSelectTasksByReporteeId, filteredTasks]
  );

  const selectTasksByAssigneeId = useMemo(
    () => createSelectTasksByAssigneeId(filteredTasks),
    [createSelectTasksByAssigneeId, filteredTasks]
  );

  const selectTasksByBoardId = useMemo(
    () => createSelectTasksByBoardId(filteredTasks),
    [createSelectTasksByBoardId, filteredTasks]
  );

  const selectTasksFromRestrictedBoards = useMemo(
    () => createSelectTasksFromRestrictedBoards(filteredTasks),
    [createSelectTasksFromRestrictedBoards, filteredTasks]
  );

  const selectTasksByCustomerId = useMemo(
    () => createSelectTasksByCustomerId(filteredTasks),
    [createSelectTasksByCustomerId, filteredTasks]
  );

  const selectTasksByPriority = useMemo(
    () => createSelectTasksByPriority(filteredTasks),
    [createSelectTasksByPriority, filteredTasks]
  );

  const selectTasksByCustomDropdownFieldValue = useMemo(
    () => createSelectTasksByCustomDropdownFieldValue(filteredTasks),
    [createSelectTasksByCustomDropdownFieldValue, filteredTasks]
  );

  return {
    tasks: filteredTasks,
    allTaskEntities,
    selectTasksByDueDateInterval,
    selectOverdueTasks,
    selectNoDueDateTasks,
    selectTasksByLabelId,
    selectTasksByStatusId,
    selectTasksByStatusName,
    selectTasksByPriority,
    selectTasksByReporteeId,
    selectTasksByAssigneeId,
    selectTasksByBoardId,
    selectTasksFromRestrictedBoards,
    selectTasksByCustomerId,
    selectTasksByCustomDropdownFieldValue,
    selectTasksByCalendarDay,
  };
};
